import { z } from 'zod'

import { lenientList, moneySchema } from '@/schemas/common'
import { isoDateTimeSchema } from '@/schemas/newsletter'
import { gatewayHandoffSchema } from '@/schemas/payments'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * Subscriptions — `/api/v5/subscriptions/`, in the app's own vocabulary.
 *
 * Wire shapes live in `@/api/schema` and are never restated here; this file
 * describes what the app holds AFTER `subscriptionService`'s mappers have run,
 * so every name is camelCase and every price is a branded `Money`.
 *
 * Rules the whole file turns on, each checked against mm-be's `dev` branch
 * (`services/subscriptions/`, d28ebaa) and the live API (2026-10-01):
 *
 *  1. TIERS ARE STRINGS. `free | bull | shark` today, but the backend owns the
 *     list, so a new tier must not fail a parse — the same call
 *     `authSubscriptionSchema` makes. `KNOWN_TIERS` is what the screens can
 *     draw; `features/account/plan.ts` turns a tier into the word a member
 *     reads. The API's `title` ("Bull") and `name` ("Shak Yearly") are NOT
 *     mapped: the owner ruled they never reach a screen.
 *  2. A FREE MEMBER HAS A ~10-YEAR EXPIRY. Registration writes a 3650-day Free
 *     row, and nothing auto-renews, so `activeUntil` is the wire's
 *     `expires_on` for a paid tier and `null` for Free — "Active until 2036"
 *     cannot be drawn by mistake. `is_active` is always true and is dropped.
 *  3. THE HAND-OFF IS A UNION ON `method`, shared with every other product
 *     sold through the gateways, so it lives in `schemas/payments.ts` with
 *     the gateway list.
 *  4. CONFIRM SUCCESS MUST SURVIVE PARSING. A 200 from `checkout/confirm/`
 *     means the plan was granted; a cosmetic field that fails its shape is
 *     caught to a fallback rather than turning a paid plan into an error.
 */

// ── Vocabulary ───────────────────────────────────────────────────────────────

/** The tiers the web can present. A plan on any other tier is not drawn. */
export const KNOWN_TIERS = ['free', 'bull', 'shark'] as const
export const knownTierSchema = z.enum(KNOWN_TIERS)
export type KnownTier = z.infer<typeof knownTierSchema>

/**
 * `payments[].status`, Title Case on the wire. Kept a string on the row, since
 * legacy rows predate v5; these are the three v5 itself writes.
 */
export const PAYMENT_STATUSES = ['Pending', 'Completed', 'Cancelled'] as const

const idSchema = z.number().int().positive()

// ── Catalogue ────────────────────────────────────────────────────────────────

/**
 * `total` is what is charged (`amount` + `vat`). `was` is the strike-through,
 * set only when the list price exceeds `total`. `vat` is `"0.00"` on every dev
 * row; a VAT line is drawn only when it is above zero.
 */
export const planPriceSchema = z.object({
  amount: moneySchema,
  vat: moneySchema,
  total: moneySchema,
  was: moneySchema.nullable(),
})

/**
 * One plan. `periodDays` is the period axis — `billing_cycle` is null for
 * 3-month plans, so it is not mapped. `allowSignal` marks the "with AI
 * signals" variant of a tier. `appOnly` plans are refused by web checkout.
 */
export const planSchema = z.object({
  id: idSchema,
  tier: z.string(),
  price: planPriceSchema,
  periodDays: z.number().int().min(0),
  isFree: z.boolean(),
  isTrial: z.boolean(),
  appOnly: z.boolean(),
  allowSignal: z.boolean(),
  displayOrder: z.number().int(),
})

/**
 * A feature's flag as mm-be reports it. The tri-state, and why `limit: 0` on
 * an enabled flag is read as "included": `features/subscriptions/lib/features.ts`.
 */
export const featureLimitSchema = z.object({
  enabled: z.boolean(),
  limit: z.number().nullable(),
})

/** Every key is optional on the wire — a plan with no feature row sends `{}`. */
export const featureMapSchema = z.record(z.string(), featureLimitSchema)

export const planDetailSchema = z.object({
  plan: planSchema,
  features: featureMapSchema,
})

// ── The member ───────────────────────────────────────────────────────────────

/**
 * `GET subscriptions/me/` — the source for "current plan + active until"
 * (rule 2). `planId` joins to the catalogue (or `plans/{id}` for Free and for
 * a retired plan still held); `planTitle` ("Bull") is only the fallback when
 * that join finds nothing, and goes through `planTitleName` before display.
 * `features` carries `signals`, which `plans/{id}` never does. `enforced`
 * false means the server refuses nothing — lock nothing on `features` alone.
 */
export const entitlementSchema = z.object({
  tier: z.string(),
  planId: idSchema.nullable(),
  planTitle: z.string(),
  activeUntil: isoDateTimeSchema.nullable(),
  features: featureMapSchema,
  enforced: z.boolean(),
})

/**
 * One attempt from `GET subscriptions/payments/` — newest first, at most 100,
 * Pending and Cancelled attempts included. `method` is the legacy spelling
 * ("Esewa"); `features/payments/lib/gateways.ts` names it. `completedOn` is
 * the UTC date.
 */
export const paymentRecordSchema = z.object({
  transactionId: z.string().min(1),
  planId: idSchema.nullable(),
  planTitle: z.string(),
  amount: moneySchema.nullable(),
  method: z.string().nullable(),
  status: z.string(),
  startedOn: isoDateTimeSchema,
  completedOn: isoDateSchema.nullable(),
})

// Both lists are lenient because dev holds a malformed legacy row of each kind —
// a negative `period_days` on an old `subscription_period`, an empty
// `transaction_id` on a legacy `product_id` — and one of them must not blank the
// entire catalogue or payment history (W1 review, minor #9).

/** `plans/` as a list: one bad plan row is dropped, not fatal to the catalogue. */
export const planListSchema = lenientList(planSchema, 'plan')
/** `payments/` as a list: one bad payment row is dropped, not fatal to the history. */
export const paymentRecordListSchema = lenientList(paymentRecordSchema, 'payment')

// ── Discounts ────────────────────────────────────────────────────────────────

/**
 * What a code takes off. `payable` is what would be charged and may be
 * `"0.00"` — checkout then refuses (`PLAN_NOT_PURCHASABLE`), so a green
 * preview is not a promise. `kind` stays a string: a new kind must not fail a
 * checkout whose Pending row is already written.
 */
export const discountSchema = z.object({
  kind: z.string(),
  code: z.string(),
  amountOff: moneySchema,
  payable: moneySchema,
})

export const discountPreviewSchema = z.object({
  planId: idSchema,
  listPrice: moneySchema,
  discount: discountSchema,
})

// ── Checkout ─────────────────────────────────────────────────────────────────

/**
 * The 201 from `checkout/`. `transactionId` is exactly what `confirm` wants:
 * our 32-hex id for eSewa, 20 hex for ConnectIPS, Khalti's `pidx` for Khalti.
 * `amount` is what will be charged, discount applied.
 */
export const checkoutStartSchema = z.object({
  transactionId: z.string().min(1),
  amount: moneySchema,
  gateway: z.string(),
  handoff: gatewayHandoffSchema,
  discount: discountSchema.nullable(),
})

/**
 * The 200 from `checkout/confirm/` — rule 4: only `transactionId` is strict.
 * `alreadyCompleted` true is the same success (a refresh, a second tab, the
 * sweeper got there first), never a second charge. `activeUntil` can be null
 * on that branch; read `me` then.
 */
export const purchaseResultSchema = z.object({
  transactionId: z.string().min(1),
  planId: idSchema.nullable().catch(null),
  planTitle: z.string().catch(''),
  activeUntil: isoDateTimeSchema.nullable().catch(null),
  alreadyCompleted: z.boolean().catch(false),
})

export type PlanPrice = z.infer<typeof planPriceSchema>
export type Plan = z.infer<typeof planSchema>
export type FeatureLimit = z.infer<typeof featureLimitSchema>
export type FeatureMap = z.infer<typeof featureMapSchema>
export type PlanDetail = z.infer<typeof planDetailSchema>
export type Entitlement = z.infer<typeof entitlementSchema>
export type PaymentRecord = z.infer<typeof paymentRecordSchema>
export type Discount = z.infer<typeof discountSchema>
export type DiscountPreview = z.infer<typeof discountPreviewSchema>
export type CheckoutStart = z.infer<typeof checkoutStartSchema>
export type PurchaseResult = z.infer<typeof purchaseResultSchema>
